import { StatusBar, Style } from '@capacitor/status-bar';
import { isNative } from './runtime.ts';

export type Theme = 'system' | 'light' | 'dark';
export type EffectiveTheme = 'light' | 'dark';

const STORAGE_KEY = 'completto:theme';
const THEMES: readonly Theme[] = ['system', 'light', 'dark'] as const;

const THEME_COLOR = {
  light: '#EFE3CB',
  dark: '#1A1A1F',
} as const;

type Listener = (theme: Theme, effective: EffectiveTheme) => void;
const listeners = new Set<Listener>();
let systemMql: MediaQueryList | null = null;

function isTheme(v: unknown): v is Theme {
  return typeof v === 'string' && (THEMES as readonly string[]).includes(v);
}

export function getTheme(): Theme {
  if (typeof localStorage === 'undefined') return 'system';
  const v = localStorage.getItem(STORAGE_KEY);
  return isTheme(v) ? v : 'system';
}

export function getEffectiveTheme(theme: Theme = getTheme()): EffectiveTheme {
  if (theme === 'system') {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}

function syncMetaThemeColor(effective: EffectiveTheme): void {
  if (typeof document === 'undefined') return;
  // 라이트/다크 별 media meta는 index.html이 유지하므로 동적 단일 meta는 건드리지 않음.
  // 다만 시스템이 아닌 사용자가 명시 토글한 경우 현재 effective를 우선 노출하기 위해
  // media 없는 단일 meta를 갱신.
  const head = document.head;
  let dynamic = head.querySelector<HTMLMetaElement>('meta[name="theme-color"]:not([media])');
  if (!dynamic) {
    dynamic = document.createElement('meta');
    dynamic.name = 'theme-color';
    head.appendChild(dynamic);
  }
  dynamic.content = THEME_COLOR[effective];
}

function syncNativeStatusBar(effective: EffectiveTheme): void {
  if (!isNative()) return;
  void StatusBar.setStyle({ style: effective === 'dark' ? Style.Dark : Style.Light }).catch(
    () => undefined,
  );
}

export function applyTheme(theme: Theme = getTheme()): EffectiveTheme {
  const effective = getEffectiveTheme(theme);
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('dark', effective === 'dark');
  }
  syncMetaThemeColor(effective);
  syncNativeStatusBar(effective);
  return effective;
}

export function setTheme(theme: Theme): EffectiveTheme {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, theme);
  }
  const effective = applyTheme(theme);
  listeners.forEach((fn) => {
    fn(theme, effective);
  });
  return effective;
}

export function cycleTheme(): EffectiveTheme {
  const current = getTheme();
  const idx = THEMES.indexOf(current);
  const next = THEMES[(idx + 1) % THEMES.length];
  return setTheme(next);
}

export function subscribeTheme(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// system 모드에서 OS 라이트/다크 설정 변경 시 자동 반영.
export function initThemeSystemWatcher(): void {
  if (systemMql) return;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  systemMql = window.matchMedia('(prefers-color-scheme: dark)');
  systemMql.addEventListener('change', () => {
    if (getTheme() === 'system') {
      const effective = applyTheme('system');
      listeners.forEach((fn) => {
        fn('system', effective);
      });
    }
  });
}
